import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { CollectionType } from './inventory/CollectionSelector';
import { ViewMode, SortOption, SortDirection } from '../types/inventory';
import ModalCloseButton from './ModalCloseButton';
import '../styles/components/displayModal.css';

export type { ViewMode, SortOption, SortDirection };

interface DisplayModalProps {
  isOpen: boolean;
  onClose: () => void;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  sortBy: SortOption;
  onSortByChange: (sort: SortOption) => void;
  sortDirection: SortDirection;
  onSortDirectionChange: (direction: SortDirection) => void;
  collection: CollectionType;
  thumbnailSize?: 'small' | 'medium' | 'large' | 'x-large';
  onThumbnailSizeChange?: (size: 'small' | 'medium' | 'large' | 'x-large') => void;
  onResetThisCollection?: () => void;
  onResetAllCollections?: () => void;
  collectionType?: 'shopping_list' | 'regular';
}

const DisplayModal: React.FC<DisplayModalProps> = ({
  isOpen,
  onClose,
  viewMode,
  onViewModeChange,
  sortBy,
  onSortByChange,
  sortDirection,
  onSortDirectionChange,
  collection,
  thumbnailSize = 'medium',
  onThumbnailSizeChange,
  onResetThisCollection,
  onResetAllCollections,
  collectionType = 'regular',
}) => {

  // Load Keep Open preference from localStorage (default: false - reversed logic)
  const [keepOpen, setKeepOpen] = useState<boolean>(() => {
    const saved = localStorage.getItem('display-modal-keep-open');
    return saved === 'true'; // Default false (unchecked)
  });

  // Save preference to localStorage
  useEffect(() => {
    localStorage.setItem('display-modal-keep-open', String(keepOpen));
  }, [keepOpen]);

  if (!isOpen) return null;

  // Get available view modes based on collection
  // Note: This needs to be updated to receive collection properties
  // For now, we detect mode from current viewMode
  const getViewModes = () => {
    // If current view mode is stock-checklist, show shopping list options
    if (viewMode === 'stock-checklist-card' || viewMode === 'stock-checklist-list') {
      return [
        { id: 'stock-checklist-card' as ViewMode, label: 'Shopping List Card', icon: 'card' },
        { id: 'stock-checklist-list' as ViewMode, label: 'Shopping List List', icon: 'list' },
      ];
    }
    // If current view mode is stock, show stock options
    if (viewMode === 'stock-card' || viewMode === 'stock-list') {
      return [
        { id: 'stock-card' as ViewMode, label: 'Stock Card', icon: 'card' },
        { id: 'stock-list' as ViewMode, label: 'Stock List', icon: 'list' },
      ];
    }
    // If current view mode is checklist, show checklist options
    if (viewMode === 'checklist-card' || viewMode === 'checklist-list') {
      return [
        { id: 'checklist-card' as ViewMode, label: 'Checklist Card', icon: 'card' },
        { id: 'checklist-list' as ViewMode, label: 'Checklist List', icon: 'list' },
      ];
    }
    // If current view mode is marketplace, show marketplace options
    if (viewMode === 'marketplace-card' || viewMode === 'marketplace-list') {
      return [
        { id: 'marketplace-card' as ViewMode, label: 'Marketplace Card', icon: 'card' },
        { id: 'marketplace-list' as ViewMode, label: 'Marketplace List', icon: 'list' },
      ];
    }
    // Default: regular card/list
    return [
      { id: 'card' as ViewMode, label: 'Card', icon: 'card' },
      { id: 'list' as ViewMode, label: 'List', icon: 'list' },
    ];
  };

  const viewModes = getViewModes();

  const handleViewModeChange = (mode: ViewMode) => {
    onViewModeChange(mode);
    if (!keepOpen) {
      onClose();
    }
  };

  const handleSortByChange = (option: SortOption) => {
    onSortByChange(option);
    if (!keepOpen) {
      onClose();
    }
  };

  const handleSortDirectionChange = () => {
    const newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
    onSortDirectionChange(newDirection);
    if (!keepOpen) {
      onClose();
    }
  };

  const modalContent = (
    <div className="modal-overlay display-modal-overlay" onClick={onClose}>
      <div className="modal-container display-modal" onClick={(e) => e.stopPropagation()}>
        <ModalCloseButton onClick={onClose} style={{ position: 'absolute', top: '16px', right: '16px', zIndex: 1 }} />
        <div className="modal-header">
          <h3>Sort Options</h3>
        </div>

        <div className="modal-body">
          {/* Sort Options */}
          <div className="display-section">
            <h4>Sort By</h4>
            <div className="sort-options">
              <select
                className="sort-select select-standard"
                value={sortBy}
                onChange={(e) => handleSortByChange(e.target.value as SortOption)}
              >
                {viewMode.startsWith('stock-checklist-') ? (
                  <>
                    <option value="shopping-list">Shopping List</option>
                    <option value="name">Name</option>
                    <option value="category">Category</option>
                    <option value="location">Location</option>
                    <option value="quantity">Quantity</option>
                    <option value="created">Date Created</option>
                    <option value="updated">Date Updated</option>
                  </>
                ) : (
                  <>
                    <option value="name">Name</option>
                    <option value="category">Category</option>
                    <option value="location">Location</option>
                    <option value="quantity">Quantity</option>
                    <option value="created">Date Created</option>
                    <option value="updated">Date Updated</option>
                  </>
                )}
              </select>
              <button
                className="sort-direction-btn"
                onClick={handleSortDirectionChange}
              >
                {sortDirection === 'asc' ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="12" y1="19" x2="12" y2="5"></line>
                    <polyline points="5 12 12 5 19 12"></polyline>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="12" y1="5" x2="12" y2="19"></line>
                    <polyline points="19 12 12 19 5 12"></polyline>
                  </svg>
                )}
                <span>{sortDirection === 'asc' ? 'Ascending' : 'Descending'}</span>
              </button>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          {/* Reset Buttons */}
          <div className="reset-buttons">
            {onResetThisCollection && (
              <button className="reset-btn" onClick={onResetThisCollection}>
                Reset This Collection
              </button>
            )}
            {onResetAllCollections && (
              <button className="reset-btn" onClick={onResetAllCollections}>
                Reset All Collections
              </button>
            )}
          </div>
          
          {/* Keep Open Row */}
          <div className="confirm-selection-row">
            <label className="confirm-selection-checkbox">
              <input
                type="checkbox"
                checked={keepOpen}
                onChange={(e) => {
                  const newValue = e.target.checked;
                  setKeepOpen(newValue);
                  localStorage.setItem('display-modal-keep-open', String(newValue));
                }}
              />
              <span>Keep Open</span>
            </label>
            {keepOpen && (
              <button className="modal-btn-primary" onClick={onClose}>
                Done
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default DisplayModal;
